import { useNavigate } from "react-router-dom";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Edit, HardDrive, Trash } from "lucide-react";
import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ASSET_REGISTRY } from "../../constants/api";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";

const AssetRegistry = () => {
    const { update } = useApiMethods()
    const [assetRegistry, setAssetRegistry] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false)
    let navigate = useNavigate()

    const { edit: editPermission, add: addPermission, draft: draftPermission, active: isActive } = checkUserPermissionFunc("Asset Management", "Asset Registry", "SubMenu");

    const handleConfirmDelete = async () => {
        setLoading(true)
        const payload = {
            id: selectedRow?.id,
            is_active: false,
            draft_delete: true,
            is_draft: true,
        }

        try {
            const response = await update(ASSET_REGISTRY, payload)
            if (response && response.status === "success") {
                ToastMsg("success", response.message);
                setLoading(false)
            } else {
                ToastMsg("error", response.message);
                setLoading(false)
            }
        } catch (error) {
            ToastMsg('error', error.message)
            setLoading(false)
        }
    }

    const columns = [
        {
            accessor: 'asset_id',
            Header: 'Asset Id',
            filterable: true,
            sortable: true,
            Cell: ({ value }) => value ?? '-'
        },
        {
            accessor: 'asset_name',
            Header: 'Asset Name',
            filterable: true,
            sortable: true,
            Cell: ({ value }) => value ?? '-'
        },
        {
            accessor: 'model_name',
            Header: 'Model Name',
            filterable: true,
            Cell: ({ value }) => value ?? '-'
        },
        {
            accessor: 'category_name',
            Header: 'Category Name',
            Cell: ({ value }) => value ?? '-'
        },
        {
            accessor: 'sub_category_name',
            Header: 'Sub Category Name',
            Cell: ({ value }) => value ?? '-'
        },
        {
            accessor: 'purchase_date',
            Header: 'Purchase Date',
            filterable: true,
            sortable: true,
            Cell: ({ value }) => value ?? '-'
        },
        {
            accessor: "is_active",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: 'Action',
                Header: 'Action',
                Cell: ({ value, row }) => {

                    const handleEdit = (row) => {
                        navigate('/assetmanagement/assetregistry/add', { state: { fromEdit: true, editDetails: row, isActive: isActive } })
                    }

                    const handleDelete = (row) => {
                        setIsDeleteConfirmOpen(true)
                        setSelectedRow(row)
                    }

                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={() => handleEdit(row)}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                            {
                                (row?.is_draft === true && draftPermission) ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                        onClick={() => handleDelete(row)}
                                    >
                                        <Trash className="h-4 w-4" />
                                    </Button>
                                ) : ('')
                            }
                        </div>
                    )
                }
            }
        ] : [])
    ]

    // add navigate screen
    const handleAddRegistry = () => {
        navigate('/assetmanagement/assetregistry/add')
    }

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={HardDrive}
                title={'Asset Registry'}
                subtitle={'Asset Registry'}
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                buttonName="Add Asset Registry"
                setOpenDialog={handleAddRegistry}
            />

            <Reactable
                columns={columns}
                data={assetRegistry}
                setData={setAssetRegistry}
                setTotalCount={setTotalRecords}
                url={ASSET_REGISTRY}
                serverSide
                isCSVRequired
                isPDFRequired
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
            />

            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Asset Registry"
                description="Are you sure you want to delete this asset registry entry? This action cannot be undone."
                type="warning"
                confirmlabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
}

export default AssetRegistry;